"use client";

import { useEffect, useState } from "react";
import { generateHeadingId, type HeadingEntry } from "@/components/content/headings";
import { scrollIntoViewMotionSafe } from "@/lib/motion";

const WORDS_PER_MINUTE = 230;

export interface ArticleOutline {
  headings: HeadingEntry[];
  readMinutes: number;
}

function outlineHeadings(article: HTMLElement): HTMLElement[] {
  return Array.from(article.querySelectorAll<HTMLElement>("h2, h3, h4")).filter(
    (element) =>
      !element.closest("[data-not-typeset], .not-typeset, details") &&
      (element.textContent?.trim() ?? "") !== "",
  );
}

function uniqueHeadingId(
  documentRoot: Document,
  text: string,
  tracker: Map<string, number>,
): string {
  let candidate = generateHeadingId(text, tracker);
  while (documentRoot.getElementById(candidate)) {
    candidate = generateHeadingId(text, tracker);
  }
  return candidate;
}

const MIN_OUTLINE_ENTRIES = 2;

function headingLevel(element: HTMLElement): number {
  return Number(element.tagName[1]);
}

function outlineLevel(elements: HTMLElement[]): number | null {
  const levels = [...new Set(elements.map(headingLevel))].sort();
  return (
    levels.find(
      (level) =>
        elements.filter((element) => headingLevel(element) === level).length >=
        MIN_OUTLINE_ENTRIES,
    ) ?? null
  );
}

export function readOutline(article: HTMLElement): ArticleOutline {
  const tracker = new Map<string, number>();
  const elements = outlineHeadings(article);
  const level = outlineLevel(elements);
  const headings: HeadingEntry[] = [];
  for (const element of elements) {
    const text = element.textContent?.trim() ?? "";
    if (!element.id) element.id = uniqueHeadingId(article.ownerDocument, text, tracker);
    element.classList.add("scroll-mt-24");
    if (headingLevel(element) === level) headings.push({ id: element.id, text });
  }
  const words = (article.innerText ?? "").split(/\s+/).filter(Boolean).length;
  return { headings, readMinutes: Math.max(1, Math.round(words / WORDS_PER_MINUTE)) };
}

export function useArticleOutline(articleId: string): ArticleOutline | null {
  const [outline, setOutline] = useState<ArticleOutline | null>(null);
  useEffect(() => {
    const frame = window.requestAnimationFrame(() => {
      const article = document.getElementById(articleId);
      if (article) setOutline(readOutline(article));
    });
    return () => window.cancelAnimationFrame(frame);
  }, [articleId]);
  return outline;
}

export function useActiveHeading(headings: HeadingEntry[]): string | null {
  const [activeId, setActiveId] = useState<string | null>(null);
  useEffect(() => {
    if (headings.length === 0) return;
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) setActiveId(entry.target.id);
        }
      },
      { rootMargin: "-96px 0px -66% 0px" },
    );
    for (const heading of headings) {
      const element = document.getElementById(heading.id);
      if (element) observer.observe(element);
    }
    return () => observer.disconnect();
  }, [headings]);
  return activeId;
}

export function jumpToHeading(headingId: string) {
  const element = document.getElementById(headingId);
  if (!element) return;
  scrollIntoViewMotionSafe(element);
  history.replaceState(null, "", `#${headingId}`);
}
